import { extractStructuredApiError } from '@/lib/api-error'

/**
 * SEC — **ce que l'écran de connexion dit d'un REFUS, écrit une seule fois pour
 * les trois portes du back-office.**
 *
 * · `POST /auth/login` — le sous-domaine de l'organisme (`AuthContext.login`),
 *   la porte la plus utilisée ;
 * · `POST /auth/login-portal`, puis `POST /auth/login-portal/select` — le
 *   portail centralisé (`Login.tsx`).
 *
 * Chaque porte portait sa propre table, et toutes rendaient « Adresse e-mail ou
 * mot de passe incorrect » (`login.error`) à des refus qui n'en sont pas :
 * · un 5xx — `503 SERVICE_BUSY` d'un pool saturé, `500` d'une erreur de base.
 *   Le 2026-09-23, seul le portail avait été corrigé : la porte du sous-domaine
 *   le disait encore ;
 * · un `429 RATE_LIMIT_EXCEEDED`. Le budget (`RateLimitType.LOGIN`, 5 essais par
 *   15 min) est lié à l'ADRESSE, compté AVANT le mot de passe, et PARTAGÉ par les
 *   trois portes du personnel ET par la connexion à un espace externe
 *   (`api/portal/login.py`) : au sixième essai, le BON mot de passe reçoit ce refus.
 * La personne retapait un mot de passe juste. (Choisir un nouveau mot de passe
 * par « mot de passe oublié » vide ce compteur : `_password_reset_confirm.py`.)
 *
 * ⚠️ Le STATUT se lit AVANT le corps : un 502, un 504 ou un 429 rendu par un
 * proxy porte du HTML, et un corps illisible (le 405 d'un hébergeur statique)
 * n'est pas un mot de passe faux non plus.
 *
 * ⚠️ Les clés s'écrivent en `messageKey: '…'` LITTÉRAL : c'est une forme que le
 * gardien `i18n-keys-exist` collecte. Il ne voit ni `t(variable)` ni
 * `setError('…')`.
 */
export type RefusDeConnexion =
    /**
     * 🔴 A33 — l'adresse n'est pas vérifiée, et le mot de passe était JUSTE (la
     * route ne nomme ce motif qu'après la vérification). Ce compte lisait
     * « Votre compte a été désactivé » : le créateur d'organisme EST
     * l'administrateur. ⛔ Aucun message ici : la vraie phrase et le renvoi du
     * lien vivent dans `RenvoiDuLienDeVerification`. `messageKey` NUL tient le
     * contrat de `useAuth().error` : aucun lecteur n'y lit « incorrect ».
     */
    | { readonly adresseNonVerifiee: true; readonly messageKey: null }
    | { readonly adresseNonVerifiee: false; readonly messageKey: string }

/**
 * Ce qu'un refus dit AVANT tout code : son STATUT, puis la LISIBILITÉ de son
 * corps. Commun aux QUATRE portes — les trois du personnel et la connexion à un
 * espace externe (`pages/portal/PortalLogin.tsx`), qui a ses propres phrases
 * mais pas sa propre règle :
 * · `indisponible` — un 5xx, ou un corps illisible ;
 * · `tentatives` — un 429, que son corps soit du JSON ou non ;
 * · `jugee` — le serveur a jugé la tentative, et son corps décide de la phrase.
 */
export type NatureDuRefus =
    | { readonly nature: 'indisponible' }
    | { readonly nature: 'tentatives' }
    | { readonly nature: 'jugee'; readonly corps: unknown }

const INDISPONIBLE: NatureDuRefus = { nature: 'indisponible' }
const TENTATIVES: NatureDuRefus = { nature: 'tentatives' }

export async function lireLaNatureDuRefus(response: Response): Promise<NatureDuRefus> {
    if (response.status >= 500) return INDISPONIBLE
    if (response.status === 429) return TENTATIVES
    try {
        return { nature: 'jugee', corps: await response.json() }
    } catch {
        return INDISPONIBLE
    }
}

const SERVICE_INDISPONIBLE: RefusDeConnexion = {
    adresseNonVerifiee: false,
    messageKey: 'login.serviceUnavailable',
}
const TROP_DE_TENTATIVES: RefusDeConnexion = {
    adresseNonVerifiee: false,
    messageKey: 'login.tooManyAttempts',
}
const COMPTE_SUSPENDU: RefusDeConnexion = {
    adresseNonVerifiee: false,
    messageKey: 'login.accountSuspended',
}
const REFUS_GENERIQUE: RefusDeConnexion = { adresseNonVerifiee: false, messageKey: 'login.error' }
const ADRESSE_NON_VERIFIEE: RefusDeConnexion = { adresseNonVerifiee: true, messageKey: null }

/**
 * AUCUNE réponse lisible : le réseau est coupé, ou le navigateur a REJETÉ la
 * réponse. En production, c'est aussi le sort du 500 générique : son gestionnaire
 * (`@app.exception_handler(Exception)`) vit sur la couche la plus externe de
 * Starlette, HORS du CORS — il sort sans `Access-Control-Allow-Origin`, et l'appel
 * `{slug}.argalis.app` → `api.argalis.app` est rejeté avant d'être lu. Même phrase
 * qu'un 5xx lu, aux trois portes : « Erreur de connexion réseau » d'un côté et
 * « service indisponible » de l'autre disaient deux choses d'un même serveur.
 */
export const REFUS_SANS_REPONSE: RefusDeConnexion = SERVICE_INDISPONIBLE

/** Lit une réponse NON-ok d'une porte de connexion et dit ce que l'écran doit en dire. */
export async function lireLeRefusDeConnexion(response: Response): Promise<RefusDeConnexion> {
    const lu = await lireLaNatureDuRefus(response)
    if (lu.nature === 'indisponible') return SERVICE_INDISPONIBLE
    if (lu.nature === 'tentatives') return TROP_DE_TENTATIVES
    const { code } = extractStructuredApiError(lu.corps)
    if (code === 'EMAIL_NOT_VERIFIED') return ADRESSE_NON_VERIFIEE
    if (code === 'ACCOUNT_INACTIVE') return COMPTE_SUSPENDU
    return REFUS_GENERIQUE
}
